import React, { useState } from 'react';
import { Container, Box, Typography } from '@mui/material';
import ClinicLinkInputForm from './components/ClinicLinkInputForm';
import PatientInfoForm from './components/PatientInfoForm';
import Loader from './components/Loader';
import InvalidClinicLink from './components/InvalidClinicLink';
import FinalLinkQr from './components/FinalLinkQr';
import { FormField, FormFieldOption, ClinicDetails } from './types';
import { trackEvent } from './utils/analytics';

function App() {
  const [validUrl, setValidUrl] = useState('');
  const [fields, setFields] = useState<FormField[]>([]);
  const [lookups, setLookups] = useState<Record<string, FormFieldOption[]>>({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [finalUrl, setFinalUrl] = useState('');

  const onLinkSubmit = async (link: string) => {
    const apiEndpoint = import.meta.env.VITE_API_URL || 'https://uat.videocall.org.au/api-slink/';
    setFinalUrl('');
    setMessage('');
    setFields([]);
    setLoading(true);

    try {
      const trimmedLink = link.trim();
      const response = await fetch(
        `${apiEndpoint}?clinic-url=${encodeURIComponent(trimmedLink)}`
      );
      const clinicDetails: ClinicDetails = await response.json();
      setValidUrl(clinicDetails.valid_url || '');
      setFields(clinicDetails.fields || []);
      setLookups(clinicDetails.lookups || {});
      setMessage(clinicDetails.message || '');

      if (clinicDetails.message) {
        trackEvent('link_gen_invalid_clinic_link');
      }
    } catch (error) {
      console.error('Error fetching clinic details:', error);
      setMessage('An error occurred while fetching clinic details');
      trackEvent('link_gen_invalid_clinic_link');
    } finally {
      setLoading(false);
    }
  };

  const onPatientInfoSubmit = (patientInfo: FormData, offlineQueue: boolean) => {
    if (!validUrl) return;

    const url = new URL(validUrl);
    url.searchParams.set('skipSetup', '1');
    url.searchParams.set('countryCode', '61');

    const additional: Record<string, string> = {};

    patientInfo.forEach((value, key) => {
      if (key === 'firstName' || key === 'lastName') {
        url.searchParams.set(key, value as string);
      } else if (key === 'validatedContactNumber' && offlineQueue) {
        url.searchParams.set(key, value as string);
      } else {
        additional[key] = value as string;
      }
    });

    url.searchParams.set('additional', encodeURIComponent(JSON.stringify(additional)));
    setFinalUrl(url.toString());

    trackEvent('link_gen_final_link_created');
    if (offlineQueue) {
      trackEvent('link_gen_offline_queue_used');
    }
  };

  const onPatientInfoChange = () => {
    setFinalUrl('');
    setMessage('');
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box className="App">
        <Typography variant="h1" sx={{ mb: 3 }}>
          Video Call Accessible Link Generator
        </Typography>
          <>
            <ClinicLinkInputForm 
              validUrl={validUrl} 
              onLinkSubmit={onLinkSubmit} 
              loading={loading}
            />
            {message && <InvalidClinicLink />}
            {fields.length > 0 && (
              <PatientInfoForm
                fields={fields}
                lookups={lookups}
                onPatientInfoSubmit={onPatientInfoSubmit}
                onPatientInfoChange={onPatientInfoChange}
              />
            )}
            {finalUrl && <FinalLinkQr finalLink={finalUrl} />}
          </>
      </Box>
    </Container>
  );
}

export default App;
